import {
  LogLevel,
  PublicClientApplication,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { AnalyticsService } from 'src/Analytics/AnalyticsService';
import { GE_DOMAIN } from './Config/constants';

let lastActivityTimer = false;
const reactAppEnvironment = process.env.REACT_APP_ENV;
// Browser check variables
// If you support IE, our recommendation is that you sign-in using Redirect APIs
// If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
const ua = window.navigator.userAgent;
const msie = ua.indexOf('MSIE ');
const msie11 = ua.indexOf('Trident/');
const msedge = ua.indexOf('Edge/');
const firefox = ua.indexOf('Firefox');
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0; // Only needed if you need to support the redirect flow in Firefox incognito
/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: process.env.REACT_APP_B2C_USER_FLOW,
  },
  authorities: {
    signUpSignIn: {
      authority: process.env.REACT_APP_AUTHORITY_URL,
    },
  },
  authorityDomain: process.env.REACT_APP_AUTHORITY_DOMAIN,
};
// Add here the endpoints to call API.
export const apiConfig = {
  b2cScopes: process.env.REACT_APP_B2C_SCOPE.split('+'),
  webApi: process.env.REACT_APP_DOMAIN,
};

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
  scopes: ['openid', ...apiConfig.b2cScopes],
  extraQueryParameters: { domain_hint: process.env.REACT_APP_SSO_DOMAIN_HINT },
  // extraQueryParameters: {domain_hint: 'DE_SFDC_DEV'},
};

export const tokenRequest = {
  scopes: [...apiConfig.b2cScopes], // e.g. ["https://fabrikamb2c.onmicrosoft.com/helloapi/demo.read"]
  forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
};
// GE SSO logout URL
export const ssoLogoutURL = process.env.REACT_APP_SSO_LOGOUT_URL;
const loginUrl = process.env.REACT_APP_LOGIN_URL;
// Config object to be passed to Msal on creation
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_B2C_CLIENT_ID, // This is the ONLY mandatory field; everything else is optional.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose sign-up/sign-in user-flow as your default.
    knownAuthorities: [b2cPolicies.authorityDomain], // You must identify your tenant's domain as a known authority.
    redirectUri: process.env.REACT_APP_REDIRECT_URL, // You must register this URI on Azure Portal/App Registration. Defaults to "window.location.href".
    logoutRedirectUri: process.env.REACT_APP_LOGOUT_URL,
    // redirectUri: "https://localhost:3000/redirect/b2c/", // You must register this URI on Azure Portal/App Registration. Defaults to "window.location.href".
    // logoutRedirectUri: "http://localhost:3000/logout"
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // If you wish to store cache items in cookies as well as browser cache, set this to "true".
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

export function signIn(domainHint, notNewPasswordRedirect = true, email = null, language='en') {
  localStorage.setItem('loginType', domainHint);
  localStorage.setItem('isLoggedOut', false);
  let unAuthLang = localStorage.getItem('unAuthLang')
  if(!unAuthLang){
    unAuthLang ='en'
  }
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */
  loginRequest.extraQueryParameters = { domain_hint: domainHint };
  if (domainHint == process.env.REACT_APP_SFDC_DOMAIN_HINT) {
    loginRequest.extraQueryParameters.login_hint = email ? encodeURIComponent(`app=0sp0c0000008OpL&userName=${email}&display=DEWeb&language=${language}`) : `app%3D0sp0c0000008OpL%26username%3Ddefault%40ge.com%26display%3DDEWeb%26language%3D${unAuthLang}`;
    if (notNewPasswordRedirect) {
      loginRequest.extraQueryParameters.prompt = 'login';
    }
  }
  msalInstance.loginRedirect(loginRequest);
}

export function signOut(fromNative = false, nativeRedirUrl = '') {
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */
  localStorage.setItem('isLoggedOut', true);
  localStorage.removeItem('activeItem');
  localStorage.removeItem(`${reactAppEnvironment}_last_activity`);
  clearInterval(lastActivityTimer);
  let loginType = localStorage.getItem('loginType');
  let logoutRequest = {
    postLogoutRedirectUri: fromNative ? nativeRedirUrl : msalConfig.auth.logoutRedirectUri,
  };
  if (loginType == GE_DOMAIN) {
    logoutRequest.postLogoutRedirectUri = fromNative ? nativeRedirUrl : ssoLogoutURL + encodeURI(msalConfig.auth.logoutRedirectUri);
  }
  AnalyticsService.trackEvent('logout');
  msalInstance.logoutRedirect(logoutRequest);
}

export async function getAccessToken() {
  let accessToken = '';
  const account = msalInstance.getActiveAccount();
  if (!account) {
    signOut();
  } else {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });
      accessToken = response.accessToken;
    } catch (error) {
      signOut();
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        return msalInstance.acquireTokenRedirect({
          ...tokenRequest,
          account: account,
        });
      }
    }
  }
  return accessToken;
}

export function selectAccount() {
  /**
   * See here for more information on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  const currentAccounts = msalInstance.getAllAccounts();
  if (currentAccounts.length < 1) {
    return;
  } else if (currentAccounts.length > 1) {
    /**
     * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
     * is cached as a new account, which results in more than one account in the cache. Here we make
     * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
     * as this is the default flow the user initially signed-in with.
     */
    const accounts = currentAccounts.filter(
      (account) =>
        account.homeAccountId
          .toUpperCase()
          .includes(b2cPolicies.names.signUpSignIn.toUpperCase()) &&
        account.idTokenClaims.iss
          .toUpperCase()
          .includes(b2cPolicies.authorityDomain.toUpperCase()) &&
        account.idTokenClaims.aud === msalConfig.auth.clientId
    );
    if (accounts.length > 1) {
      // localAccountId identifies the entity for which the token asserts information.
      if (
        accounts.every(
          (account) => account.localAccountId === accounts[0].localAccountId
        )
      ) {
        // All accounts belong to the same user
        msalInstance.setActiveAccount(accounts[0]);
      } else {
        // Multiple users detected. Logout all to be safe.
        signOut();
      }
    } else if (accounts.length === 1) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  } else if (currentAccounts.length === 1) {
    msalInstance.setActiveAccount(currentAccounts[0]);
  }
}

function setAccount(event){
  if(event.payload?.account?.idTokenClaims['tfp'].toUpperCase() === b2cPolicies.names.signUpSignIn.toUpperCase()){
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
  } else selectAccount();
}

function setLoginFailure(event){
  if (event.error.errorCode === 'server_error') 
    window.location.href = `${window.location.origin}` + '/noAccess.html';
}

function setToken(event){
  if (!lastActivityTimer) {
    if (localStorage.getItem(`${reactAppEnvironment}_last_activity`) && ((Date.now() - localStorage.getItem(`${reactAppEnvironment}_last_activity`)) >= 35000)) {
      localStorage.setItem('isAutomaticLogout', true);
      signOut();
    }
    else {
      localStorage.setItem(`${reactAppEnvironment}_last_activity`, Date.now());
      lastActivityTimer = setInterval(() => { localStorage.setItem(`${reactAppEnvironment}_last_activity`, Date.now()); }, 15000);
    }
  }
  /*Code added to signout from web app on navigation to Profile or Support page 
    from native app in the scenario where the logged in user is different from the user Profile 
    selected in Native app*/ 
  const search = window.location?.search;
  const email = new URLSearchParams(search).get("email");
  const validPath = window.location.pathname;
  if (email && (validPath === "/nativeAccount" || validPath === "/nativeSupport") && (email.toLowerCase() !== event.payload?.account?.idTokenClaims?.email?.toLowerCase()))
    signOut(true,window.location.href);
  
  //End of Code
}

msalInstance.addEventCallback((event) => {
  switch(event.eventType){
    case EventType.LOGIN_SUCCESS:
      setAccount(event);
      break;
    case EventType.LOGIN_FAILURE:
      setLoginFailure(event);
      break;
    case EventType.ACQUIRE_TOKEN_SUCCESS:
      setToken(event);
    break;
    default:
  }
});

export { msalInstance };
